/* eslint-disable max-statements */
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

// types
import type { Ref, ComputedRef } from 'vue'
import type { AvatarPathPoint } from '@/customTypes/index'

// Services
import Logger from '@/services/Logger.js'
const logger: any = new Logger('DevStore', true)

export const useDevStore = defineStore('dev', () => {
  const isLocalDevSession: boolean =
    _ENVIRONMENT_ === 'local-development' || _ENVIRONMENT_ === 'development'
  const showDemoControls: Ref<boolean> = ref(isLocalDevSession)
  const isDemoSession: Ref<boolean> = ref(false)

  const devStoreAvailable: ComputedRef<boolean> = computed(
    () => _ENVIRONMENT_ === 'local-development' || showDemoControls.value
  )
  // _______________ MODIFY THESE _______________
  const defaultDevValues = {
    skipScreenerIntro: false,
    skipSubtestIntro: false,
    sendAnswerToApi: true,
    ignoreApiAnswers: false,
    stopOnNextStep: false,
    skipWaitTime: false,
    drawProgressiveMapPath: false,
    forceFullscreen: !(_ENVIRONMENT_ === 'local-development')
  }

  // ________ Live Environment Demo Override _________
  const overrideValues: Ref<Partial<typeof defaultDevValues>> = ref({})

  // _______________DON'T MODIFY THESE _______________
  const computedValues = {
    skipScreenerIntro: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.skipScreenerIntro ?? defaultDevValues.skipScreenerIntro
        : false
    ),
    skipSubtestIntro: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.skipSubtestIntro ?? defaultDevValues.skipSubtestIntro
        : false
    ),
    sendAnswerToApi: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.sendAnswerToApi ?? defaultDevValues.sendAnswerToApi
        : true
    ),
    ignoreApiAnswers: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.ignoreApiAnswers ?? defaultDevValues.ignoreApiAnswers
        : false
    ),
    stopOnNextStep: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.stopOnNextStep ?? defaultDevValues.stopOnNextStep
        : false
    ),
    skipWaitTime: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.skipWaitTime ?? defaultDevValues.skipWaitTime
        : false
    ),
    forceFullscreen: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.forceFullscreen ?? defaultDevValues.forceFullscreen
        : true
    ),
    drawProgressiveMapPath: computed(() =>
      devStoreAvailable.value
        ? overrideValues.value.drawProgressiveMapPath ?? defaultDevValues.drawProgressiveMapPath
        : false
    )
  }

  // Other properties
  const activeSubtestIndex: number = 0
  const activeQuestionIndex: number = 0
  const isPracticeQuestion: boolean = true
  const nextStepReady: Ref<boolean> = ref(false)

  // Override function
  function overrideDevValues(newValues: Partial<typeof defaultDevValues>): void {
    if (devStoreAvailable.value) {
      overrideValues.value = { ...overrideValues.value, ...newValues }
      logger.info('Dev values overridden:', overrideValues.value)
    } else {
      logger.warn('Attempted to override dev values in non-development environment')
    }
  }

  function setNextStepReady(value: boolean): void {
    logger.info('Stopped on next step')
    nextStepReady.value = value
  }

  function getNextStepReady(): boolean {
    return nextStepReady.value
  }

  function setDemoSession(value: boolean) {
    isDemoSession.value = value
    if (!value && isLocalDevSession) return // If dev/local session use default dev values
    showDemoControls.value = value
    overrideDevValues({ sendAnswerToApi: !value })
  }

  /* NOTE: Used for development purposes to show the path the avatar is walking along maybe useful if the progressive map changes */
  function createTempDevPath(pathPoints: any) {
    if (!isLocalDevSession || !computedValues.drawProgressiveMapPath.value) {
      return
    }
    const existingContainer = document.getElementById('debug-path-container')
    if (existingContainer) {
      existingContainer.remove()
    }

    const container = document.createElement('div')
    container.id = 'debug-path-container'
    container.style.position = 'fixed'
    container.style.top = '0'
    container.style.left = '0'
    container.style.right = '0'
    container.style.bottom = '0'
    container.style.pointerEvents = 'none'
    container.style.zIndex = '999999'

    pathPoints.forEach((point: AvatarPathPoint, index: number) => {
      const pointElement = document.createElement('div')
      pointElement.style.position = 'absolute'
      pointElement.style.width = '6px'
      pointElement.style.height = '6px'
      // highlights any path areas which fall into flip zones
      if (point.inFlipZone) {
        pointElement.style.backgroundColor = 'rgba(0, 0, 255, 0.5)'
      } else {
        pointElement.style.backgroundColor = 'rgba(255, 80, 80, 0.5)'
      }
      pointElement.style.borderRadius = '50%'
      pointElement.style.transform = 'translate(-50%, -50%)'
      pointElement.style.left = `${point.x}px`
      pointElement.style.top = `${point.y}px`
      pointElement.title = `Point ${index}: (${Math.round(point.x)}, ${Math.round(point.y)})`
      container.appendChild(pointElement)

      if (index < pathPoints.length - 1) {
        const nextPoint = pathPoints[index + 1]
        const length = Math.sqrt(
          Math.pow(nextPoint.x - point.x, 2) + Math.pow(nextPoint.y - point.y, 2)
        )
        const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x)

        const lineElement = document.createElement('div')
        lineElement.style.position = 'absolute'
        lineElement.style.height = '1px'
        lineElement.style.backgroundColor = 'rgba(0, 0, 255, 0.3)'
        lineElement.style.width = `${length}px`
        lineElement.style.left = `${point.x}px`
        lineElement.style.top = `${point.y}px`
        lineElement.style.transform = `rotate(${angle}rad)`
        lineElement.style.transformOrigin = '0 0'
        container.appendChild(lineElement)
      }
    })

    if (pathPoints.length > 0) {
      const startPoint = pathPoints[0]
      const startMarker = document.createElement('div')
      startMarker.style.position = 'absolute'
      startMarker.style.width = '8px'
      startMarker.style.height = '8px'
      startMarker.style.backgroundColor = 'rgba(0, 255, 0, 0.7)'
      startMarker.style.borderRadius = '50%'
      startMarker.style.transform = 'translate(-50%, -50%)'
      startMarker.style.left = `${startPoint.x}px`
      startMarker.style.top = `${startPoint.y}px`
      startMarker.title = `Start: (${Math.round(startPoint.x)}, ${Math.round(startPoint.y)})`
      container.appendChild(startMarker)

      const endPoint = pathPoints[pathPoints.length - 1]
      const endMarker = document.createElement('div')
      endMarker.style.position = 'absolute'
      endMarker.style.width = '8px'
      endMarker.style.height = '8px'
      endMarker.style.backgroundColor = 'rgba(255, 0, 0, 0.7)'
      endMarker.style.borderRadius = '50%'
      endMarker.style.transform = 'translate(-50%, -50%)'
      endMarker.style.left = `${endPoint.x}px`
      endMarker.style.top = `${endPoint.y}px`
      endMarker.title = `End: (${Math.round(endPoint.x)}, ${Math.round(endPoint.y)})`
      container.appendChild(endMarker)
    }

    document.body.appendChild(container)
  }

  return {
    devStoreAvailable,
    ...computedValues,
    activeSubtestIndex,
    activeQuestionIndex,
    isPracticeQuestion,
    isLocalDevSession,
    isDemoSession,
    showDemoControls,
    setNextStepReady,
    getNextStepReady,
    overrideDevValues,
    setDemoSession,
    createTempDevPath
  }
})
